export interface Component {
    shipmentId: string;
    productId: string;
    componentId: string;
    tcId: string;
    tcAssociatedProductNo: string;
    tcProductComponentOrder: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentNetShippingWeight: number;
    tcComponentSupplementaryWeight: number;
  }
  
  // The API response is an array of Component objects.
  export type ApiGetComponentList = Component[];

  // Interface for component standard information-------------------------------------------------------------
export interface ComponentStandardInformation {
    tcComponentStandardLabelOrder: string;
    tcComponentStandardLabelGrade: string;
    tcComponentCertifiedWeight: number;
    additionalProp1: Record<string, any>;
  }
  
  // Interface for component raw material information
  export interface ComponentRawMaterialInformation {
    tcComponentRawMaterialOrder: string;
    tcComponentRawMaterialPercentage: number;
    tcComponentRawMaterialCode: string;
    additionalProp1: Record<string, any>;
  }
  
  // Interface for the individual component information object
  export interface TcComponentInformation {
    tcProductComponentOrder: string;
    tcAssociatedProductNo: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentInfoTrimsExcluded: string;
    tcComponentInfoAssociatedTc: string;
    tcComponentInfoFiberLength: string;
    tcComponentInfoYarnCount: string;
    tcComponentInfoFabricWeightAndConstruct: string;
    tcComponentInfoFabricWeightOfFinishedProduct: string;
    tcComponentNetShippingWeight: number;
    tcComponentSupplementaryWeight: number;
    tcComponentInfoAdditional: string;
    tcProductComponentStandardInformation: ComponentStandardInformation[];
    tcComponentRawMaterialInformation: ComponentRawMaterialInformation[];
    additionalProp1: Record<string, any>;
  }
  
  // Main interface for the API response
  export interface ApiGetAdditionalData {
    tcId: string;
    tcComponentInformation: TcComponentInformation[];
    additionalProp1: Record<string, any>;
    componentId: string;
    shipmentId: string;
    productId: string;
  }
  //-----------------------------------------------------------------------------

  export interface ComponentInfo {
    tcAssociatedProductNo: string;
    tcProductComponentOrder: string;
    tcProductComponentNo: string;
    tcComponentDetail: string;
    tcComponentInfoTrimsExcluded: string;
    tcComponentInfoAssociatedTc: string;
    tcComponentInfoFiberLength: string;
    tcComponentInfoYarnCount: string;
    tcComponentInfoFabricWeightAndConstruct: string;
    tcComponentInfoFabricWeightOfFinishedProduct: string;
    tcComponentNetShippingWeight: number | null;
    tcComponentSupplementaryWeight: number | null;
    tcComponentInfoAdditional: string;
  }
  
  export interface StandardInfo {
    tcComponentStandardLabelOrder: number|null;
    tcComponentStandardLabelGrade: string;
    tcComponentCertifiedWeight: string|null;
  }
  
  export interface RawMaterialInfo {
    tcComponentRawMaterialOrder: number|null;
    tcComponentRawMaterialPercentage: string|null;
    tcComponentRawMaterialCode: string;
  }
  
  export interface ApiComponentData {
    componentInfo: ComponentInfo;
    standardInfo: StandardInfo[];
    rawMaterialInfo: RawMaterialInfo[];
  }
  
  export interface MappedComponentPayload {
    tcId: string;
    tcComponentInformation: Array<{
      tcProductComponentOrder: string;
      tcAssociatedProductNo: string;
      tcProductComponentNo: string;
      tcComponentDetail: string;
      tcComponentInfoTrimsExcluded: string;
      tcComponentInfoAssociatedTc: string;
      tcComponentInfoFiberLength: string;
      tcComponentInfoYarnCount: string;
      tcComponentInfoFabricWeightAndConstruct: string;
      tcComponentInfoFabricWeightOfFinishedProduct: string;
      tcComponentNetShippingWeight: number | null;
      tcComponentSupplementaryWeight: number | null;
      tcComponentInfoAdditional: string;
      tcProductComponentStandardInformation: Array<{
        tcComponentStandardLabelOrder: string;
        tcComponentStandardLabelGrade: string;
        tcComponentCertifiedWeight: number;
        additionalProp1: Record<string, any>;
      }>;
      tcComponentRawMaterialInformation: Array<{
        tcComponentRawMaterialOrder: string|null;
        tcComponentRawMaterialPercentage: number|null;
        tcComponentRawMaterialCode: string;
        additionalProp1: Record<string, any>;
      }>;
      additionalProp1: Record<string, any>;
    }>;
    additionalProp1: Record<string, any>;
  }

  export interface ProductShipmentResponse {
    shipmentId: string;
    productId: string;
    tcId: string;
    tcProductShipmentNo: string[];
    tcProductNo: string;
    tcProductCategoryCode: string;
    tcNetShippingWeight: number;
    tcProductProductionDate: string; // Format: YYYY-MM-DD
    tcProductSupplementaryWeight: number;
    tcProductOrderNo: string[];
    tcProductArticleNo: string;
    tcProductNumberOfUnits: number;
    tcProductUnitsMetric: string;
    tcProductInfoAssociatedTc: string;
  }
  
  /**
   * Finds a shipment by its tcProductNo and returns an object containing its productId and shipmentId.
   *
   * @param data - Array of ProductShipmentResponse objects.
   * @param tcProductNo - The product number to look up.
   * @returns An object with productId and shipmentId if found, otherwise undefined.
   */
  export const getProductShipmentDataByProductNo = (
    data: any[],
    tcProductNo: string
  ): { productId: string; shipmentId: string[]; tcProductShipmentNo: string[] } | undefined => {
    const shipments = data.filter((shipment) => shipment.tcProductNo === tcProductNo);
    return shipments
      ? { 
          productId: shipments[0].productId, 
          shipmentId: shipments.map(({shipmentId})=>shipmentId), 
          tcProductShipmentNo: shipments.map(({tcAssociatedProductShipmentNo})=>tcAssociatedProductShipmentNo[0]) 
        }
      : undefined;
  };
  
  /**
 * Maps an ApiGetAdditionalData object to an ApiComponentData object.
 *
 * @param data - The input API data.
 * @returns The mapped ApiComponentData.
 */
  export const mapToApiComponentData = (data: Partial<ApiGetAdditionalData> | TcComponentInformation): any => {
    // If data.tcComponentInformation exists and has items, use its first element; otherwise, assume data is already the component object.
    const sourceComponent: TcComponentInformation =
      'tcComponentInformation' in data && Array.isArray(data.tcComponentInformation) && data.tcComponentInformation.length > 0
        ? data.tcComponentInformation[0]
        : (data as TcComponentInformation);
  
    // Map the componentInfo by converting numeric weight fields to strings.
    const componentInfo: ComponentInfo = {
      tcAssociatedProductNo: sourceComponent.tcAssociatedProductNo,
      tcProductComponentOrder: sourceComponent.tcProductComponentOrder,
      tcProductComponentNo: sourceComponent.tcProductComponentNo,
      tcComponentDetail: sourceComponent.tcComponentDetail,
      tcComponentInfoTrimsExcluded: sourceComponent.tcComponentInfoTrimsExcluded,
      tcComponentInfoAssociatedTc: sourceComponent.tcComponentInfoAssociatedTc,
      tcComponentInfoFiberLength: sourceComponent.tcComponentInfoFiberLength,
      tcComponentInfoYarnCount: sourceComponent.tcComponentInfoYarnCount,
      tcComponentInfoFabricWeightAndConstruct: sourceComponent.tcComponentInfoFabricWeightAndConstruct,
      tcComponentInfoFabricWeightOfFinishedProduct: sourceComponent.tcComponentInfoFabricWeightOfFinishedProduct,
      tcComponentNetShippingWeight: sourceComponent.tcComponentNetShippingWeight,
      tcComponentSupplementaryWeight: sourceComponent.tcComponentSupplementaryWeight,
      tcComponentInfoAdditional: sourceComponent.tcComponentInfoAdditional,
    };
  
    // Map standard info: convert order to number and certified weight to string.
    const standardInfo: StandardInfo[] = sourceComponent.tcProductComponentStandardInformation.map((item) => ({
      tcComponentStandardLabelOrder: item.tcComponentStandardLabelOrder?Number(item.tcComponentStandardLabelOrder):null,
      tcComponentStandardLabelGrade: item.tcComponentStandardLabelGrade,
      tcComponentCertifiedWeight: item.tcComponentCertifiedWeight?String(item.tcComponentCertifiedWeight):null,
    }));
  
    // Map raw material info: convert order and percentage appropriately.
    const rawMaterialInfo: RawMaterialInfo[] = sourceComponent.tcComponentRawMaterialInformation.map((item) => ({
      tcComponentRawMaterialOrder: item.tcComponentRawMaterialOrder?Number(item.tcComponentRawMaterialOrder):null,
      tcComponentRawMaterialPercentage: item.tcComponentRawMaterialPercentage? String(item.tcComponentRawMaterialPercentage):null,
      tcComponentRawMaterialCode: item.tcComponentRawMaterialCode,
    }));
  
    return {
      componentInfo,
      standardInfo,
      rawMaterialInfo,
    };
  };
  